import React from 'react';
import { familleCompte } from '../../lib/modulesCatalog';
import { AbsenceCodesSettings } from '../Planning/AbsenceCodesSettings';
import { useLocation } from 'react-router-dom';
import { Settings, User as UserIcon, Receipt, CalendarClock, Clock, Bell, Plus, Trash2, Loader2, Plug, FileText, FileCheck, X, Globe, Code2, Copy, Check, Stamp, Landmark, Car, Mail, ClipboardCheck, UserCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { authApi, planningApi, moduleApi } from '../../lib/api';
import { BillingSettingsModal } from '../Factures/BillingSettingsModal';
import { NotificationsOnglet } from './NotificationsOnglet';
import { ApplicationsSettings } from './ApplicationsSettings';
import { EInvoicingSettings } from './EInvoicingSettings';
import { TresorerieSettings } from './TresorerieSettings';
import { EnvoisAutoSettings } from './EnvoisAutoSettings';
import { ReglagesCommerciauxPanneau } from '../Commerciaux/ReglagesCommerciaux';
import { FideliteSettings } from './FideliteSettings';
import { SimulateurWidgetSettings } from './SimulateurWidgetSettings';
import { InterventionsTab } from './InterventionsTab';
import { DocumentTemplatesSettings } from './DocumentTemplatesSettings';
import { WeeklyHours, DayKey, DAY_KEYS, DAY_LABELS, defaultWeeklyHours } from '../PrestationPlanning/prestationPlanningShared';
import { BookingRulesBlock, BookingClosuresBlock, BookingPaymentRuleSettings, BookingRules, defaultBookingRules } from './BookingRulesSettings';

// --- Onglet Compte : email + mot de passe (même logique que l'ancienne modale "Mon profil" de App.tsx,
// simplement déplacée ici) ---
export function AccountTab() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = React.useState({ email: user?.email || '', currentPassword: '', newPassword: '', confirmPassword: '' });
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');
    const [saving, setSaving] = React.useState(false);

    const isEmploye = user?.type === 'employe';
    const emailChanged = (form.email || '').trim() !== String(user?.email || '').trim();
    const wantsPasswordChange = form.newPassword.length > 0 || form.confirmPassword.length > 0;
    const isEmailRequired = user?.type !== 'collaborator' && !isEmploye;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emailError = form.email.trim()
        ? (!emailPattern.test(form.email.trim()) ? 'Format email invalide.' : '')
        : (isEmailRequired ? 'Email obligatoire pour ce compte.' : '');
    const passwordError = wantsPasswordChange && form.newPassword.length < 8
        ? 'Le mot de passe doit contenir au moins 8 caractères.'
        : '';
    const confirmError = wantsPasswordChange && form.newPassword !== form.confirmPassword
        ? 'La confirmation du mot de passe ne correspond pas.'
        : '';
    const currentPasswordError = wantsPasswordChange && !form.currentPassword
        ? 'Mot de passe actuel requis pour changer le mot de passe.'
        : '';
    const hasChanges = emailChanged || wantsPasswordChange;
    const canSave = hasChanges && !emailError && !passwordError && !confirmError && !currentPasswordError;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!canSave) return;
        try {
            setSaving(true);
            const payload = {
                newEmail: (form.email || '').trim(),
                currentPassword: form.currentPassword || undefined,
                newPassword: form.newPassword || undefined,
            };
            if (isEmploye) await authApi.updateEmployeeProfile(payload);
            else await authApi.updateProfile(payload);
            await refreshUser();
            setSuccess('Profil mis à jour avec succès.');
            setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de la mise à jour du profil.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
        <form onSubmit={handleSubmit} className="max-w-md space-y-4">
            {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>}
            {success && <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">{success}</div>}

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="nom@exemple.com" />
                {emailError && <p className="text-xs text-red-400">{emailError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Mot de passe actuel</label>
                <input type="password" value={form.currentPassword} onChange={(e) => setForm((p) => ({ ...p, currentPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="Requis si changement de mot de passe" />
                {currentPasswordError && <p className="text-xs text-red-400">{currentPasswordError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Nouveau mot de passe</label>
                <input type="password" value={form.newPassword} onChange={(e) => setForm((p) => ({ ...p, newPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors"
                    placeholder="Minimum 8 caractères" />
                {passwordError && <p className="text-xs text-red-400">{passwordError}</p>}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Confirmer le nouveau mot de passe</label>
                <input type="password" value={form.confirmPassword} onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-surface-soft border border-border text-content outline-none focus:border-brand-500 transition-colors" />
                {confirmError && <p className="text-xs text-red-400">{confirmError}</p>}
            </div>

            <button type="submit" disabled={!canSave || saving}
                className="px-5 py-3 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
        </form>
        <BookingWidgetSection />
        </>
    );
}

// --- Widget "Réserver en ligne" (bouton flottant à intégrer sur le site du propriétaire) — regroupé dans
// "Compte" plutôt que dans "Application" (qui ne concerne que des outils tiers connectés, pas un widget
// propre à L'IAmani). Trois cas : client Prestation (catalogue), client Restauration avec RDV Pro actif, et
// super admin (widget personnel pour démarcher de potentiels futurs clients — bascule + libellé de bouton
// personnalisable, puisqu'il n'a pas de module "rdv_pro" à activer/désactiver comme un établissement).
function WidgetCard({ title, description, snippet, copied, onCopy }: { title: string; description: string; snippet: string; copied: boolean; onCopy: () => void }) {
    return (
        <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
            <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                    <Globe size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-bold text-content text-sm">{title}</p>
                    <p className="text-xs text-content-muted mt-0.5">{description}</p>
                </div>
            </div>
            <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2">
                <Code2 size={14} className="text-content-muted shrink-0" />
                <code className="text-[11px] text-content-muted break-all font-mono flex-1">{snippet}</code>
            </div>
            <p className="text-xs text-content-muted">Collez ce code juste avant la balise <code className="text-[11px] bg-surface border border-border px-1 py-0.5 rounded">&lt;/body&gt;</code> de votre site.</p>
            <button onClick={onCopy} className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                {copied ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier le code</>}
            </button>
        </div>
    );
}

function BookingWidgetSection() {
    const { user } = useAuth();
    const ownerId = user?.type === 'collaborator' ? ((user as any)?.client_id || '') : (user?.id || '');
    const isAdmin = user?.type === 'admin';
    const isPrestation = user?.type === 'client' && (user as any)?.client_type === 'prestation';
    const isRestauration = user?.type === 'client' && familleCompte((user as any)?.client_type) === 'restauration';

    const [hasRdvPro, setHasRdvPro] = React.useState(false);
    const [hasPlanningRdv, setHasPlanningRdv] = React.useState(false);
    React.useEffect(() => {
        if (!isRestauration && !isPrestation) return;
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : []).filter((m: any) => m.is_active === 1).map((m: any) => m.module_name);
            setHasRdvPro(names.includes('rdv_pro'));
            setHasPlanningRdv(names.includes('planning_rdv'));
        }).catch(() => { setHasRdvPro(false); setHasPlanningRdv(false); });
    }, [isRestauration, isPrestation]);

    // Bascule + libellé du widget RDV — réutilise directement /pro-rdv/settings, partagé par le super admin
    // (widget de démarchage), un établissement Restauration (RDV Pro) et un établissement Prestation (agenda
    // salariés, widget multi-Démarches) — même contrôle pour les trois, résolu côté serveur selon le compte
    // connecté. Sans cette bascule accessible, `pro_rdv_widget_enabled` restait bloqué à 0 par défaut pour un
    // client Prestation dès qu'une ligne client_settings existait pour lui (ex: horaires Planning déjà
    // configurés) — le lien "Envoyer un lien de rendez-vous" échouait alors toujours avec "non disponible".
    const canControlRdvWidget = isAdmin || (isRestauration && hasRdvPro) || (isPrestation && hasPlanningRdv);
    const [rdvWidgetEnabled, setRdvWidgetEnabled] = React.useState(false);
    const [rdvWidgetLabel, setRdvWidgetLabel] = React.useState('');
    const [rdvWidgetLoading, setRdvWidgetLoading] = React.useState(true);
    const [rdvWidgetSaving, setRdvWidgetSaving] = React.useState(false);
    React.useEffect(() => {
        if (!canControlRdvWidget) { setRdvWidgetLoading(false); return; }
        moduleApi.getProRdvSettings().then((s: any) => {
            setRdvWidgetEnabled(!!s?.widget_enabled);
            setRdvWidgetLabel(s?.widget_label || '');
        }).catch(() => {}).finally(() => setRdvWidgetLoading(false));
    }, [canControlRdvWidget]);
    const saveRdvWidget = async (patch: { widget_enabled?: boolean; widget_label?: string }) => {
        setRdvWidgetSaving(true);
        try {
            await moduleApi.saveProRdvSettings(patch);
        } catch (e) {
            console.error('Failed to save widget settings', e);
        } finally {
            setRdvWidgetSaving(false);
        }
    };

    // Public visé, uniquement pour le widget Démarches (rdv-pro) : l'établissement copie un code pour
    // sa page « professionnels » et un autre pour sa page « particuliers », et pose les deux sur son
    // site. Le choix vit dans le code collé et non en base — c'est ce qui permet d'avoir les deux
    // widgets côte à côte sur deux pages du même site.
    const [snippetAudience, setSnippetAudience] = React.useState<'' | 'professionnel' | 'particulier'>('');
    const makeSnippet = (mode: 'prestation' | 'rdv-pro', label: string, audience: '' | 'professionnel' | 'particulier' = '') => ownerId
        ? `<script src="${window.location.origin}/booking-widget.js" data-client-id="${ownerId}" data-mode="${mode}" data-label="${label.replace(/"/g, '&quot;')}"${audience ? ` data-audience="${audience}"` : ''}></script>`
        : '';

    // Libellé du widget catalogue (mode 'prestation') — jusqu'ici figé en dur à "Réserver en ligne", sans
    // réglage à côté du widget RDV Pro qui, lui, a déjà le sien. Signalé par Emeni (2026-09-01) : les deux
    // widgets se ressemblaient trop à l'écran pour les distinguer, et un seul des deux boutons se renommait.
    const [catalogWidgetLabel, setCatalogWidgetLabel] = React.useState('');
    const [catalogWidgetLoading, setCatalogWidgetLoading] = React.useState(true);
    const [catalogWidgetSaving, setCatalogWidgetSaving] = React.useState(false);
    React.useEffect(() => {
        if (!isPrestation) { setCatalogWidgetLoading(false); return; }
        moduleApi.getPrestationPlanningSettings().then((s: any) => {
            setCatalogWidgetLabel(s?.booking_widget_label || '');
        }).catch(() => {}).finally(() => setCatalogWidgetLoading(false));
    }, [isPrestation]);
    const saveCatalogWidgetLabel = async (label: string) => {
        setCatalogWidgetSaving(true);
        try {
            await moduleApi.savePrestationPlanningSettings({ booking_widget_label: label });
        } catch (e) {
            console.error('Failed to save catalog widget label', e);
        } finally {
            setCatalogWidgetSaving(false);
        }
    };

    const [copiedKey, setCopiedKey] = React.useState<string | null>(null);
    const handleCopy = async (key: string, text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 2000);
        } catch (e) {}
    };

    if (!isPrestation && !(canControlRdvWidget && !rdvWidgetLoading)) return null;

    return (
        <div className="max-w-md space-y-4 mt-8">
            {/* Les widgets se sont accumulés (reservation, RDV Pro, simulateur, avis) et se ressemblaient
                tous a l'ecran : meme icone, meme pave de code, aucun moyen de savoir lequel fait quoi ni ou
                le poser. Un en-tete les annonce, et chaque carte porte desormais une pastille disant si
                elle pose un bouton flottant ou si elle s'affiche dans la page. Demande d'Emeni (2026-09-09). */}
            <div className="space-y-1">
                <h3 className="text-sm font-bold text-content">Vos widgets pour votre site</h3>
                <p className="text-xs text-content-muted">
                    Des bouts de code a coller sur votre propre site. Chacun fait une chose et une seule —
                    la pastille vous dit ou il se pose.{isPrestation ? ' Le widget de vos avis clients, lui, se copie depuis le module Avis.' : ''}
                </p>
            </div>
            {isPrestation && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <Globe size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-content text-sm">Reservation d'une prestation</p>
                                <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-surface border border-border text-content-muted">Bouton flottant</span>
                            </div>
                            <p className="text-xs text-content-muted mt-0.5">Le visiteur choisit une prestation de votre catalogue et reserve son creneau, sans quitter votre page.</p>
                        </div>
                    </div>
                    {!catalogWidgetLoading && (
                        <>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Texte du bouton</label>
                                <div className="flex items-center gap-2">
                                    <input type="text" value={catalogWidgetLabel} onChange={(e) => setCatalogWidgetLabel(e.target.value)}
                                        onBlur={() => saveCatalogWidgetLabel(catalogWidgetLabel)}
                                        placeholder="Réserver en ligne" maxLength={60}
                                        className="flex-1 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                    {catalogWidgetSaving && <Loader2 size={14} className="animate-spin text-content-muted shrink-0" />}
                                </div>
                            </div>
                            <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2">
                                <Code2 size={14} className="text-content-muted shrink-0" />
                                <code className="text-[11px] text-content-muted break-all font-mono flex-1">{makeSnippet('prestation', catalogWidgetLabel || 'Réserver en ligne')}</code>
                            </div>
                            <p className="text-xs text-content-muted">Collez ce code juste avant la balise <code className="text-[11px] bg-surface border border-border px-1 py-0.5 rounded">&lt;/body&gt;</code> de votre site.</p>
                            <button onClick={() => handleCopy('prestation', makeSnippet('prestation', catalogWidgetLabel || 'Réserver en ligne'))}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                {copiedKey === 'prestation' ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier le code</>}
                            </button>
                        </>
                    )}
                </div>
            )}
            {canControlRdvWidget && !rdvWidgetLoading && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-500 shrink-0">
                            <Globe size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-bold text-content text-sm">{(isRestauration || isPrestation) ? 'Demarches et rendez-vous (RDV Pro)' : 'Widget de reservation en ligne'}</p>
                                <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-surface border border-border text-content-muted">Bouton flottant</span>
                            </div>
                            <p className="text-xs text-content-muted mt-0.5">
                                {isAdmin
                                    ? 'Un bouton flottant sur votre propre site pour fixer des rendez-vous avec de potentiels futurs clients.'
                                    : 'Ajoutez un bouton sur votre propre site — vos visiteurs demandent un rendez-vous sans quitter votre page. Désactivez-le à tout moment sans avoir à retirer le code de votre site.'}
                            </p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                            <input type="checkbox" checked={rdvWidgetEnabled} className="sr-only peer"
                                onChange={(e) => { setRdvWidgetEnabled(e.target.checked); saveRdvWidget({ widget_enabled: e.target.checked }); }} />
                            <div className="w-10 h-6 bg-surface rounded-full border border-border peer-checked:bg-brand-600 transition-all after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4" />
                        </label>
                    </div>
                    {rdvWidgetEnabled && (
                        <>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Texte du bouton</label>
                                <div className="flex items-center gap-2">
                                    <input type="text" value={rdvWidgetLabel} onChange={(e) => setRdvWidgetLabel(e.target.value)}
                                        onBlur={() => saveRdvWidget({ widget_label: rdvWidgetLabel })}
                                        placeholder="Réserver en ligne" maxLength={60}
                                        className="flex-1 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                    {rdvWidgetSaving && <Loader2 size={14} className="animate-spin text-content-muted shrink-0" />}
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Public de ce widget</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {([['', 'Tous'], ['professionnel', 'Professionnels'], ['particulier', 'Particuliers']] as const).map(([v, lab]) => (
                                        <button key={v || 'tous'} type="button" onClick={() => setSnippetAudience(v)}
                                            className={`rounded-lg border px-2 py-2 text-xs font-bold transition-all ${snippetAudience === v ? 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300' : 'border-border text-content-muted hover:text-content'}`}>
                                            {lab}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-[11px] text-content-muted">
                                    {snippetAudience === ''
                                        ? 'Ce code affiche toutes vos Démarches. Choisissez un public pour obtenir un code à poser sur la page correspondante de votre site — vous pouvez copier les deux, l\'un après l\'autre.'
                                        : 'Ce code n\'affiche que les Démarches de ce public, plus celles ouvertes aux deux. Si vous n\'en avez aucune, le bouton ne s\'affiche pas du tout sur votre site.'}
                                </p>
                            </div>
                            <div className="bg-surface border border-border rounded-xl p-3 flex items-center gap-2">
                                <Code2 size={14} className="text-content-muted shrink-0" />
                                <code className="text-[11px] text-content-muted break-all font-mono flex-1">{makeSnippet('rdv-pro', rdvWidgetLabel || 'Réserver en ligne', snippetAudience)}</code>
                            </div>
                            <p className="text-xs text-content-muted">Collez ce code juste avant la balise <code className="text-[11px] bg-surface border border-border px-1 py-0.5 rounded">&lt;/body&gt;</code> de votre site.{isAdmin ? ' Configurez vos horaires et votre durée de rendez-vous dans l\'onglet "RDV Pro".' : ''}</p>
                            <button onClick={() => handleCopy('rdv-widget', makeSnippet('rdv-pro', rdvWidgetLabel || 'Réserver en ligne', snippetAudience))}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                                {copiedKey === 'rdv-widget' ? <><Check size={13} /> Copié</> : <><Copy size={13} /> Copier le code</>}
                            </button>
                        </>
                    )}
                </div>
            )}
            {/* Simulateur de devis : troisieme widget public, distinct des deux autres — il ne prend aucun
                rendez-vous et ne collecte rien, il estime un montant a partir du catalogue et des formules
                de l'etablissement. Choix d'Emeni (2026-09-08) : aucun bouton d'appel a l'action. */}
            {isPrestation && <SimulateurWidgetSettings ownerId={ownerId} />}
        </div>
    );
}

// --- Onglet Frais kilométriques (client Prestation) : sorti de l'onglet Horaires (2026-10-10, demande
// d'Emeni : « je ne veux pas que ça soit dans Horaires »). Tarif au km, trajet, point de départ, zone
// gratuite autour de l'adresse, seuil d'offre. Enregistre uniquement les réglages de facturation. ---
function FraisKilometriquesTab() {
    const [billingSettings, setBillingSettings] = React.useState<Record<string, any>>({});
    const [locationMode, setLocationMode] = React.useState<'domicile' | 'entreprise' | 'les_deux'>('domicile');
    const [kmFeeEnabled, setKmFeeEnabled] = React.useState(false);
    const [kmFeePricePerKm, setKmFeePricePerKm] = React.useState('');
    const [kmFeeAllerRetour, setKmFeeAllerRetour] = React.useState(true);
    // Point de départ des déplacements (2026-09-24, Emeni) : l'établissement, ou le domicile du salarié
    // assigné au rendez-vous.
    const [kmOriginMode, setKmOriginMode] = React.useState<'etablissement' | 'salarie'>('etablissement');
    // Le calcul de distance dépend d'une clé d'itinéraire réglée par L'IAmani. Sans elle, les frais
    // tombaient à 0 sans rien dire : l'établissement croyait facturer ses déplacements (2026-09-24).
    const [kmRoutingAvailable, setKmRoutingAvailable] = React.useState(true);
    // Zone gratuite autour de l'adresse (2026-10-10, Emeni : « mon client facture à partir de 10 km »).
    const [kmFreeRadius, setKmFreeRadius] = React.useState('');
    const [kmFreeRadiusMode, setKmFreeRadiusMode] = React.useState<'excedent' | 'total'>('excedent');
    // Seuil d'offre des frais kilométriques (2026-09-25, Emeni) : offerts au-delà d'un montant TTC de
    // devis, pour les particuliers, les professionnels, ou les deux.
    const [kmWaiverEnabled, setKmWaiverEnabled] = React.useState(false);
    const [kmWaiverThreshold, setKmWaiverThreshold] = React.useState('');
    const [kmWaiverThresholdPro, setKmWaiverThresholdPro] = React.useState('');
    const [kmWaiverMaxKm, setKmWaiverMaxKm] = React.useState('20');
    const [kmWaiverAudience, setKmWaiverAudience] = React.useState<'particulier' | 'professionnel' | 'les_deux' | 'separe'>('les_deux');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        Promise.all([
            moduleApi.getPrestationPlanningSettings(),
            moduleApi.getBillingSettings(),
        ]).then(([settings, billing]: any[]) => {
            if (settings?.location_mode) setLocationMode(settings.location_mode);
            setBillingSettings(billing || {});
            setKmFeeEnabled(!!billing?.km_fee_enabled);
            setKmFeePricePerKm(billing?.km_fee_price_per_km ? String(billing.km_fee_price_per_km) : '');
            setKmFeeAllerRetour(billing?.km_fee_aller_retour !== 0);
            setKmOriginMode(billing?.km_origin_mode === 'salarie' ? 'salarie' : 'etablissement');
            setKmRoutingAvailable(billing?.km_routing_available !== false);
            setKmFreeRadius(billing?.km_fee_free_radius_km ? String(billing.km_fee_free_radius_km) : '');
            setKmFreeRadiusMode(billing?.km_fee_free_radius_mode === 'total' ? 'total' : 'excedent');
            setKmWaiverEnabled(!!billing?.km_fee_waiver_enabled);
            setKmWaiverThreshold(billing?.km_fee_waiver_threshold_ttc ? String(billing.km_fee_waiver_threshold_ttc) : '');
            setKmWaiverThresholdPro(billing?.km_fee_waiver_threshold_pro_ttc ? String(billing.km_fee_waiver_threshold_pro_ttc) : '');
            setKmWaiverMaxKm(billing?.km_fee_waiver_max_km === null || billing?.km_fee_waiver_max_km === undefined ? '20' : String(billing.km_fee_waiver_max_km));
            setKmWaiverAudience(['particulier', 'professionnel', 'les_deux', 'separe'].includes(billing?.km_fee_waiver_audience) ? billing.km_fee_waiver_audience : 'les_deux');
        }).catch((e: any) => console.error('Failed to load km fee settings', e)).finally(() => setLoading(false));
    }, []);

    const showKmFee = locationMode === 'domicile' || locationMode === 'les_deux';

    const handleSave = async () => {
        setSaving(true);
        try {
            // billing-settings est un enregistrement complet (PUT remplace toute la ligne) — on repart de
            // ce qui a été chargé pour ne pas écraser les autres réglages de facturation (catalogue, TVA...).
            await moduleApi.saveBillingSettings({
                ...billingSettings,
                km_fee_enabled: showKmFee && kmFeeEnabled,
                km_fee_price_per_km: Math.max(0, Number(kmFeePricePerKm) || 0),
                km_fee_aller_retour: kmFeeAllerRetour,
                km_origin_mode: kmOriginMode,
                km_fee_free_radius_km: Math.max(0, Number(kmFreeRadius) || 0),
                km_fee_free_radius_mode: kmFreeRadiusMode,
                km_fee_waiver_enabled: kmWaiverEnabled,
                km_fee_waiver_threshold_ttc: Math.max(0, Number(kmWaiverThreshold) || 0),
                km_fee_waiver_threshold_pro_ttc: Math.max(0, Number(kmWaiverThresholdPro) || 0),
                km_fee_waiver_audience: kmWaiverAudience,
                km_fee_waiver_max_km: kmWaiverMaxKm.trim() === '' ? 20 : Math.max(0, Number(kmWaiverMaxKm) || 0),
            });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save km fee settings', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-6">
            {!showKmFee && (
                <div className="p-4 rounded-2xl bg-surface-soft border border-border text-xs text-content-muted">
                    Vos interventions se font uniquement dans votre entreprise : il n'y a pas de déplacement à facturer. Passez
                    le mode d'intervention sur « Chez le client » ou « Les deux » dans l'onglet Horaires pour activer ces réglages.
                </div>
            )}
            {showKmFee && (
                <div className="space-y-2 p-4 rounded-2xl bg-surface-soft border border-border">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={kmFeeEnabled} onChange={(e) => setKmFeeEnabled(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                        <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Facturer des frais kilométriques</span>
                    </label>
                    <p className="text-xs text-content-muted italic">Calculés automatiquement à partir de la distance routière jusqu'à l'adresse du client : estimation affichée dans le simulateur et la demande de devis, montant définitif sur le devis et à la clôture du rendez-vous "chez le client".</p>
                    {kmFeeEnabled && !kmRoutingAvailable && (
                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-xs text-content">
                            <b>Le calcul automatique est indisponible.</b> La clé de calcul d'itinéraire n'est pas
                            configurée : aucune distance ne peut être mesurée, et <b>aucun frais kilométrique n'est
                            ajouté</b> à vos clôtures de rendez-vous, malgré le tarif ci-dessous. Contactez le support
                            L'IAmani pour la faire activer.
                        </div>
                    )}
                    {kmFeeEnabled && (
                        <>
                            <div className="flex items-center gap-2 pt-1">
                                <input type="number" min={0} step="0.01" value={kmFeePricePerKm} onChange={(e) => setKmFeePricePerKm(e.target.value)}
                                    placeholder="Ex: 0.50" className="w-32 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                <span className="text-xs text-content-muted">€ / km</span>
                            </div>
                            <div className="pt-3 space-y-2">
                                <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Zone gratuite autour de votre adresse</p>
                                <div className="flex items-center gap-2">
                                    <input type="number" min={0} step="1" value={kmFreeRadius} onChange={(e) => setKmFreeRadius(e.target.value)}
                                        placeholder="0" className="w-24 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                    <span className="text-xs text-content-muted">km de rayon (0 = pas de zone gratuite)</span>
                                </div>
                                {Number(kmFreeRadius) > 0 && (
                                    <>
                                        <div className="flex flex-col gap-1.5 bg-surface rounded-xl p-1 max-w-md">
                                            {([['excedent', 'Facturer seulement au-delà du rayon'], ['total', 'Facturer tout le trajet hors de la zone']] as const).map(([v, label]) => (
                                                <button key={v} type="button" onClick={() => setKmFreeRadiusMode(v)}
                                                    className={`px-3 py-2 rounded-lg text-xs font-bold text-left transition-colors ${kmFreeRadiusMode === v ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        <p className="text-xs text-content-muted italic">
                                            {kmFreeRadiusMode === 'excedent'
                                                ? `Jusqu'à ${Number(kmFreeRadius)} km : gratuit. Au-delà, seuls les kilomètres en plus des ${Number(kmFreeRadius)} premiers sont facturés (à ${Number(kmFreeRadius) + 4} km du point de départ : 4 km facturés).`
                                                : `Jusqu'à ${Number(kmFreeRadius)} km : gratuit. Au-delà, tout le trajet depuis votre adresse est facturé, sans retirer les ${Number(kmFreeRadius)} premiers (à ${Number(kmFreeRadius) + 4} km : ${Number(kmFreeRadius) + 4} km facturés).`}
                                        </p>
                                    </>
                                )}
                                <p className="text-xs text-content-muted italic">
                                    La distance mesurée est la distance routière entre le point de départ et l'adresse du client. Le client voit « offerts » quand il est dans la zone.
                                </p>
                            </div>
                            <div className="pt-3 space-y-2">
                                <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Trajet facturé</p>
                                <div className="flex gap-1.5 bg-surface rounded-xl p-1 max-w-md">
                                    {([[true, 'Aller-retour'], [false, 'Aller simple']] as const).map(([v, label]) => (
                                        <button key={String(v)} type="button" onClick={() => setKmFeeAllerRetour(v)}
                                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${kmFeeAllerRetour === v ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-content-muted italic">
                                    {kmFeeAllerRetour
                                        ? "La distance est doublée (aller + retour) pour le calcul des frais."
                                        : "Seule la distance à l'aller est facturée, sans le retour."}
                                </p>
                            </div>
                            <div className="pt-3 space-y-2">
                                <p className="text-xs font-bold text-content-muted uppercase tracking-widest">Point de départ des déplacements</p>
                                <div className="flex gap-1.5 bg-surface rounded-xl p-1 max-w-md">
                                    {([['etablissement', 'Nos locaux'], ['salarie', 'Le domicile du salarié']] as const).map(([v, label]) => (
                                        <button key={v} type="button" onClick={() => setKmOriginMode(v)}
                                            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${kmOriginMode === v ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-content-muted italic">
                                    {kmOriginMode === 'salarie'
                                        ? "La distance est comptée depuis l'adresse du salarié assigné au rendez-vous, renseignée sur sa fiche. S'il n'en a pas, ou si plusieurs salariés sont assignés, c'est l'adresse de vos locaux qui sert (le premier salarié assigné fait référence). Le point de départ retenu est écrit sur la ligne de facture."
                                        : "La distance est comptée depuis l'adresse de vos locaux, quel que soit le salarié assigné."}
                                </p>
                                <p className="text-xs text-content-muted italic">
                                    Dans les deux cas, l'estimation affichée au client quand il réserve part de vos locaux : à ce moment-là, aucun salarié n'est encore assigné. Le montant définitif est celui calculé à la clôture.
                                </p>
                            </div>
                            <div className="pt-3 space-y-2 border-t border-border">
                                <label className="flex items-center gap-2.5 cursor-pointer">
                                    <input type="checkbox" checked={kmWaiverEnabled} onChange={(e) => setKmWaiverEnabled(e.target.checked)} className="accent-brand-600 w-4 h-4" />
                                    <span className="text-xs font-bold text-content-muted uppercase tracking-widest">Offrir les frais kilométriques à partir d'un montant</span>
                                </label>
                                {kmWaiverEnabled && (
                                    <>
                                        <div className="flex gap-1.5 bg-surface rounded-xl p-1 max-w-xl">
                                            {([['particulier', 'Particuliers'], ['professionnel', 'Professionnels'], ['les_deux', 'Les deux (même montant)'], ['separe', 'Deux montants']] as const).map(([v, label]) => (
                                                <button key={v} type="button" onClick={() => setKmWaiverAudience(v)}
                                                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${kmWaiverAudience === v ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                                                    {label}
                                                </button>
                                            ))}
                                        </div>
                                        {kmWaiverAudience === 'separe' ? (
                                            <div className="space-y-2 pt-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="w-28 text-xs font-bold text-content-muted">Particuliers</span>
                                                    <input type="number" min={0} step="1" value={kmWaiverThreshold} onChange={(e) => setKmWaiverThreshold(e.target.value)}
                                                        placeholder="Ex: 500" className="w-32 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                    <span className="text-xs text-content-muted">€ TTC de devis, ou plus</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="w-28 text-xs font-bold text-content-muted">Professionnels</span>
                                                    <input type="number" min={0} step="1" value={kmWaiverThresholdPro} onChange={(e) => setKmWaiverThresholdPro(e.target.value)}
                                                        placeholder="Ex: 800" className="w-32 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                    <span className="text-xs text-content-muted">€ TTC de devis, ou plus</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 pt-1">
                                                <input type="number" min={0} step="1" value={kmWaiverThreshold} onChange={(e) => setKmWaiverThreshold(e.target.value)}
                                                    placeholder="Ex: 500" className="w-32 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                <span className="text-xs text-content-muted">€ TTC de devis, ou plus</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 pt-1">
                                            <span className="text-xs font-bold text-content-muted">Seulement si le client est à</span>
                                            <input type="number" min={0} step="1" value={kmWaiverMaxKm} onChange={(e) => setKmWaiverMaxKm(e.target.value)}
                                                placeholder="20" className="w-24 bg-surface border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                            <span className="text-xs text-content-muted">km ou moins (0 = sans limite)</span>
                                        </div>
                                        <p className="text-xs text-content-muted italic">
                                            Dès que le devis (hors frais de déplacement) atteint ce montant pour le public choisi, les frais kilométriques ne sont plus ajoutés — ni au devis, ni à la clôture du rendez-vous.
                                        </p>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}


            {showKmFee && (
                <button onClick={handleSave} disabled={saving}
                    className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                    {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
                </button>
            )}
        </div>
    );
}

// --- Onglet Horaires (client Prestation uniquement) : bornes de la frise horaire du Planning
// rendez-vous — déplacé ici depuis le modal "gear" du module Planning pour éviter le doublon avec
// cette page Paramètres centrale (même principe que Codes Absences). ---
function PlanningRdvHoursTab() {
    const { refreshUser } = useAuth();
    const [draft, setDraft] = React.useState<WeeklyHours>(defaultWeeklyHours());
    const [bookingRules, setBookingRules] = React.useState<BookingRules>(defaultBookingRules());
    const [extrasAutorises, setExtrasAutorises] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        Promise.all([
            moduleApi.getPrestationPlanningSettings(),
            moduleApi.getBillingSettings(),
        ]).then(([settings, billing]: any[]) => {
            if (settings?.weekly_hours) setDraft({ ...defaultWeeklyHours(), ...settings.weekly_hours });
            if (settings?.booking_rules) setBookingRules({ ...defaultBookingRules(), ...settings.booking_rules });
            setExtrasAutorises(!!settings?.prestation_booking_extras_allowed);
        }).catch((e: any) => console.error('Failed to load planning rdv hours', e)).finally(() => setLoading(false));
    }, []);

    const updateDay = (day: DayKey, patch: Partial<WeeklyHours[DayKey]>) => {
        setDraft((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await moduleApi.savePrestationPlanningSettings({ weekly_hours: draft, booking_rules: bookingRules, prestation_booking_extras_allowed: extrasAutorises });
            await refreshUser();
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save planning rdv hours', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-6">
            <h3 className="text-sm font-bold text-content">Horaires d'ouverture</h3>
            <p className="text-xs text-content-muted italic">Ces horaires bornent la frise horaire affichée dans le planning journalier, jour par jour.</p>
            <div className="space-y-2">
                {DAY_KEYS.map((day) => {
                    const cfg = draft[day];
                    // Ouvert 24 h/24 (2026-09-23) : un jour qui va de 00:00 à 24:00. Les champs horaires
                    // disparaissent alors — un <input type="time"> ne sait pas afficher 24:00.
                    const is24 = !cfg.closed && cfg.opening === '00:00' && cfg.closing === '24:00';
                    return (
                        <div key={day} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-2">
                            <div className="flex items-center gap-3">
                                <span className="w-24 shrink-0 text-xs font-bold text-content">{DAY_LABELS[day]}</span>
                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                    <input type="checkbox" checked={cfg.closed} onChange={(e) => updateDay(day, { closed: e.target.checked })} className="accent-brand-600" />
                                    Fermé
                                </label>
                                {!cfg.closed && (
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                        <input type="checkbox" checked={is24}
                                            onChange={(e) => updateDay(day, e.target.checked
                                                ? { opening: '00:00', closing: '24:00', hasBreak: false }
                                                : { opening: '08:00', closing: '20:00' })}
                                            className="accent-brand-600" />
                                        24 h/24
                                    </label>
                                )}
                                {!cfg.closed && !is24 && (
                                    <>
                                        <input type="time" value={cfg.opening} onChange={(e) => updateDay(day, { opening: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        <input type="time" value={cfg.closing} onChange={(e) => updateDay(day, { closing: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                    </>
                                )}
                            </div>
                            {!cfg.closed && !is24 && (
                                <div className="flex items-center gap-3 pl-[calc(6rem+0.75rem)]">
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                        <input type="checkbox" checked={!!cfg.hasBreak}
                                            onChange={(e) => updateDay(day, e.target.checked
                                                ? { hasBreak: true, breakStart: cfg.breakStart || '12:00', breakEnd: cfg.breakEnd || '14:00' }
                                                : { hasBreak: false })}
                                            className="accent-brand-600" />
                                        Inclure une pause
                                    </label>
                                    {cfg.hasBreak && (
                                        <>
                                            <input type="time" value={cfg.breakStart || ''} onChange={(e) => updateDay(day, { breakStart: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                            <span className="text-xs text-content-muted">–</span>
                                            <input type="time" value={cfg.breakEnd || ''} onChange={(e) => updateDay(day, { breakEnd: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <BookingRulesBlock rules={bookingRules} onChange={(patch) => setBookingRules((prev) => ({ ...prev, ...patch }))} />

            <div className="space-y-2 p-4 rounded-2xl bg-surface-soft border border-border">
                <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" checked={extrasAutorises} onChange={(e) => setExtrasAutorises(e.target.checked)} className="accent-brand-600 mt-0.5" />
                    <span className="text-sm text-content">
                        <b>Autoriser le client à ajouter des prestations hors formule à sa réservation</b>
                        <span className="block text-[11px] text-content-muted mt-0.5">
                            Cochée, le client peut ajouter d'autres prestations du catalogue en plus de celles convenues au devis, sur le lien de réservation ou en scannant son QR code. Facturées en supplément — avec la réduction de sa formule si la prestation en fait partie.
                        </span>
                    </span>
                </label>
            </div>

            <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>

            {/* Les fermetures s'enregistrent une par une, elles ne dépendent donc pas du bouton ci-dessus. */}
            <div className="pt-4 border-t border-border">
                <BookingClosuresBlock />
            </div>
        </div>
    );
}

// RDV Professionnel (léger) : un seul calendrier établissement, sans catalogue ni employés — proposé
// depuis une réponse à une "Documentation Établissement" (ex: privatisation). Réutilise la même structure
// WeeklyHours/DayHours que le Planning Prestation, mais dans une colonne dédiée (pro_rdv_weekly_hours).
export function ProRdvHoursTab() {
    const [draft, setDraft] = React.useState<WeeklyHours>(defaultWeeklyHours());
    const [employees, setEmployees] = React.useState<any[]>([]);
    const [eligibleIds, setEligibleIds] = React.useState<string[]>([]);
    const [employeeSearch, setEmployeeSearch] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        Promise.all([moduleApi.getProRdvSettings(), moduleApi.getEmployes().catch(() => [])]).then(([settings, emps]: any[]) => {
            if (settings?.weekly_hours) setDraft({ ...defaultWeeklyHours(), ...settings.weekly_hours });
            setEligibleIds(Array.isArray(settings?.eligible_employee_ids) ? settings.eligible_employee_ids.map(String) : []);
            setEmployees(Array.isArray(emps) ? emps : []);
        }).catch((e: any) => console.error('Failed to load pro rdv settings', e)).finally(() => setLoading(false));
    }, []);

    const updateDay = (day: DayKey, patch: Partial<WeeklyHours[DayKey]>) => {
        setDraft((prev) => ({ ...prev, [day]: { ...prev[day], ...patch } }));
    };

    const employeeName = (emp: any) => `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || 'Salarié';
    const eligibleEmployees = employees.filter((e) => eligibleIds.includes(String(e.id)));
    const searchResults = employeeSearch.trim().length > 0
        ? employees.filter((e) => !eligibleIds.includes(String(e.id)) && employeeName(e).toLowerCase().includes(employeeSearch.trim().toLowerCase())).slice(0, 8)
        : [];
    const addEligible = (id: string) => { setEligibleIds((prev) => [...prev, id]); setEmployeeSearch(''); };
    const removeEligible = (id: string) => setEligibleIds((prev) => prev.filter((e) => e !== id));

    const handleSave = async () => {
        setSaving(true);
        try {
            await moduleApi.saveProRdvSettings({ weekly_hours: draft, eligible_employee_ids: eligibleIds });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save pro rdv settings', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-6">
            <p className="text-xs text-content-muted italic">Ces horaires définissent les créneaux proposés à vos contacts lorsqu'une "Démarche client" leur permet de prendre rendez-vous (ex: privatisation). La durée d'un rendez-vous se configure directement dans chaque "Démarche client".</p>

            <div className="space-y-2">
                {DAY_KEYS.map((day) => {
                    const cfg = draft[day];
                    return (
                        <div key={day} className="p-2.5 rounded-xl bg-surface-soft border border-border space-y-2">
                            <div className="flex items-center gap-3">
                                <span className="w-24 shrink-0 text-xs font-bold text-content">{DAY_LABELS[day]}</span>
                                <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                    <input type="checkbox" checked={cfg.closed} onChange={(e) => updateDay(day, { closed: e.target.checked })} className="accent-brand-600" />
                                    Fermé
                                </label>
                                {!cfg.closed && (
                                    <>
                                        <input type="time" value={cfg.opening} onChange={(e) => updateDay(day, { opening: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        <input type="time" value={cfg.closing} onChange={(e) => updateDay(day, { closing: e.target.value })}
                                            className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                    </>
                                )}
                            </div>
                            {!cfg.closed && (
                                <div className="flex items-center gap-3 pl-[calc(6rem+0.75rem)]">
                                    <label className="flex items-center gap-1.5 text-[11px] text-content-muted cursor-pointer shrink-0">
                                        <input type="checkbox" checked={!!cfg.hasBreak}
                                            onChange={(e) => updateDay(day, e.target.checked
                                                ? { hasBreak: true, breakStart: cfg.breakStart || '12:00', breakEnd: cfg.breakEnd || '14:00' }
                                                : { hasBreak: false })}
                                            className="accent-brand-600" />
                                        Inclure une pause
                                    </label>
                                    {cfg.hasBreak && (
                                        <>
                                            <input type="time" value={cfg.breakStart || ''} onChange={(e) => updateDay(day, { breakStart: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                            <span className="text-xs text-content-muted">–</span>
                                            <input type="time" value={cfg.breakEnd || ''} onChange={(e) => updateDay(day, { breakEnd: e.target.value })}
                                                className="flex-1 min-w-0 bg-surface border border-border rounded-lg px-2 py-1.5 text-xs text-content outline-none focus:border-brand-500" />
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="space-y-2">
                <label className="text-xs font-bold text-content-muted uppercase tracking-widest">Salariés qui font les RDV Pro</label>
                <p className="text-xs text-content-muted italic">Ceux que vous pourrez assigner à un rendez-vous depuis le calendrier RDV Pro.</p>
                {employees.length === 0 ? (
                    <p className="text-xs text-content-muted">Aucun salarié disponible.</p>
                ) : (
                    <>
                        {eligibleEmployees.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {eligibleEmployees.map((emp: any) => (
                                    <span key={emp.id} className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-bold border border-brand-500/20">
                                        {employeeName(emp)}
                                        <button onClick={() => removeEligible(String(emp.id))} className="p-0.5 rounded hover:bg-brand-500/20"><X size={11} /></button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <div className="relative">
                            <input type="text" value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)}
                                placeholder="Rechercher un salarié à ajouter..."
                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            {searchResults.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full bg-surface border border-border rounded-xl shadow-xl max-h-56 overflow-y-auto">
                                    {searchResults.map((emp: any) => (
                                        <button key={emp.id} type="button" onMouseDown={() => addEligible(String(emp.id))}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-surface-soft transition-colors text-content">
                                            {employeeName(emp)}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>
        </div>
    );
}

type TabKey = 'compte' | 'facturation' | 'einvoicing' | 'tresorerie' | 'commerciaux' | 'fidelite' | 'absences' | 'horaires' | 'interventions' | 'frais_km' | 'notifications' | 'applications' | 'envois' | 'documentation';

export const SettingsPage: React.FC = () => {
    const { user } = useAuth();
    const location = useLocation();
    // Le retour d'un aller-retour OAuth (Stripe Connect, 2026-09-26) est une redirection serveur classique,
    // pas une navigation interne : location.state (utilisé partout ailleurs pour ouvrir un onglet précis)
    // ne survit pas au rechargement de page. Le paramètre d'URL ?tab= sert de repli pour ce seul cas.
    const TAB_KEYS: TabKey[] = ['compte', 'facturation', 'einvoicing', 'tresorerie', 'commerciaux', 'fidelite', 'absences', 'horaires', 'interventions', 'frais_km', 'notifications', 'applications', 'documentation'];
    const tabFromQuery = new URLSearchParams(location.search).get('tab');
    const initialTab = (location.state as { tab?: TabKey } | null)?.tab
        || (TAB_KEYS.includes(tabFromQuery as TabKey) ? (tabFromQuery as TabKey) : undefined);
    const [tab, setTab] = React.useState<TabKey>(initialTab || 'compte');
    const [activeModules, setActiveModules] = React.useState<string[]>([]);
    const [modulesLoaded, setModulesLoaded] = React.useState(false);

    React.useEffect(() => {
        authApi.getMyModules().then((modules: any) => {
            const names = (Array.isArray(modules) ? modules : [])
                .filter((m: any) => m.is_active === 1)
                .map((m: any) => m.module_name);
            setActiveModules(names);
        }).catch((e) => console.error('Failed to load active modules', e)).finally(() => setModulesLoaded(true));
    }, []);

    // Facturation/Codes Absences/Notifications : pertinents pour un client ou un collaborateur (pas un
    // salarié, qui n'a pas à configurer ces réglages) — ET seulement si le module correspondant est
    // réellement actif pour l'établissement (même logique que le verrou serveur : un module désactivé
    // masque aussi ses réglages, pas seulement ses pages). Le "Codes Absences" partagé reste visible tant
    // qu'au moins un des 3 modules qui l'utilisent est actif (planning, planning_rdv, demande_conges),
    // pour rester cohérent avec l'exemption appliquée côté serveur sur /planning/settings.
    // Les paramètres de modules (facturation, absences, notifications) sont réservés au client titulaire
    // du compte — un collaborateur ayant accès à un module au quotidien n'a pas accès à sa configuration.
    const canSeeBusinessTabs = user?.type === 'client';
    const hasModule = (name: string) => activeModules.includes(name);
    const canSeeFacturation = canSeeBusinessTabs && hasModule('facture');
    // Facturation électronique (plateforme agréée + clé API) : même porte que la Facturation — titulaire
    // du compte + module Facture actif. Écran de configuration uniquement pour l'instant.
    const canSeeEInvoicing = canSeeBusinessTabs && hasModule('facture');
    // Trésorerie : scaffold uniquement (2026-09-24), la vraie synchro bancaire n'est pas encore branchée —
    // même porte que la Facturation électronique (titulaire du compte + module actif).
    const canSeeTresorerie = canSeeBusinessTabs && hasModule('tresorerie');
    // Carte de fidélité : elle se remplit à la clôture d'un rendez-vous, donc elle n'a de sens qu'avec le
    // Planning des rendez-vous de prestation.
    const canSeeFidelite = canSeeBusinessTabs && hasModule('planning_rdv');
    // Réglages des commerciaux (taux, moment du paiement des commissions, versement) : titulaire du compte, module Commerciaux actif.
    const canSeeCommerciaux = canSeeBusinessTabs && hasModule('commerciaux');
    // Les codes d'absence vivent désormais dans les réglages du module Planning (demande d'Emeni,
    // 2026-08-25). Cet onglet ne subsiste que pour les clients SANS module Planning — Planning RDV ou
    // demandes de congés seuls — qui n'auraient sinon plus aucun endroit où les éditer. Un même client
    // ne le voit donc jamais aux deux endroits à la fois.
    const canSeeAbsences = canSeeBusinessTabs && !hasModule('planning')
        && (hasModule('planning_rdv') || hasModule('demande_conges'));
    // Horaires du Planning rendez-vous : Prestation uniquement (Restauration garde ses propres horaires
    // dans le module Planning, structure trop différente pour être fusionnée dans cette passe).
    const canSeeHoraires = canSeeBusinessTabs && user?.client_type === 'prestation' && hasModule('planning_rdv');
    // L'onglet Notifications combine deux blocs indépendants, chacun visible selon ses propres modules :
    // les emails du Planning rendez-vous (Prestation uniquement) et les emails Facture/Congés/Événementiel/
    // RDV Pro (les deux types de client, un par un selon le module actif — cf. ClientEmailNotificationsSettings).
    const canSeePrestationNotif = canSeeBusinessTabs && user?.client_type === 'prestation' && hasModule('planning_rdv');
    const canSeeClientEmailNotif = canSeeBusinessTabs && (hasModule('facture') || hasModule('demande_conges') || hasModule('evenementiel') || hasModule('crm') || (hasModule('rdv_pro') && user?.client_type === 'restauration'));
    const canSeeNotifications = canSeePrestationNotif || canSeeClientEmailNotif;
    // "Application" (connexion d'outils tiers, Stripe pour l'instant) : ouvert aux deux types de client
    // depuis l'ajout de "Démarches client" (acomptes/paiements possibles côté Restauration aussi). Corrigé
    // (bug remonté par le propriétaire) : la clé Stripe sert aussi bien aux Démarches client (module "crm",
    // acompte/paiement intégral configurable par modèle) qu'à la facturation — un établissement avec
    // Démarches actives mais Facture désactivée n'avait autrement aucun moyen de connecter Stripe.
    const canSeeApplications = canSeeBusinessTabs && (hasModule('facture') || hasModule('crm'));
    // Démarches client : liée aux contacts CRM, disponible pour les deux types de client dès que le
    // module CRM est actif. L'historique des demandes envoyées ("Demandes récurrentes") a été déplacé
    // hors des Réglages vers une tuile dédiée sur le Dashboard (App.tsx > DashboardView).
    // Envois automatiques par e-mail : visibles dès qu'au moins un document envoyable est disponible (heures, factures).
    const canSeeEnvois = canSeeBusinessTabs && ['planning', 'facture', 'demande_conges', 'livre_reservations', 'evenementiel', 'fournisseurs', 'commandes'].some(hasModule);
    const canSeeDocuments = canSeeBusinessTabs && hasModule('crm');
    const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
        { key: 'compte', label: 'Compte', icon: <UserIcon size={14} /> },
        ...(canSeeFacturation ? [{ key: 'facturation' as TabKey, label: 'Facturation', icon: <Receipt size={14} /> }] : []),
        ...(canSeeEInvoicing ? [{ key: 'einvoicing' as TabKey, label: 'Facturation électronique', icon: <FileCheck size={14} /> }] : []),
        ...(canSeeTresorerie ? [{ key: 'tresorerie' as TabKey, label: 'Trésorerie', icon: <Landmark size={14} /> }] : []),
        ...(canSeeCommerciaux ? [{ key: 'commerciaux' as TabKey, label: 'Commerciaux', icon: <UserCheck size={14} /> }] : []),
        ...(canSeeFidelite ? [{ key: 'fidelite' as TabKey, label: 'Fidélité', icon: <Stamp size={14} /> }] : []),
        ...(canSeeAbsences ? [{ key: 'absences' as TabKey, label: 'Codes Absences', icon: <CalendarClock size={14} /> }] : []),
        ...(canSeeHoraires ? [{ key: 'horaires' as TabKey, label: 'Horaires', icon: <Clock size={14} /> }] : []),
        ...(canSeeHoraires ? [{ key: 'interventions' as TabKey, label: 'Interventions', icon: <ClipboardCheck size={14} /> }] : []),
        ...(canSeeHoraires ? [{ key: 'frais_km' as TabKey, label: 'Frais kilométriques', icon: <Car size={14} /> }] : []),
        ...(canSeeNotifications ? [{ key: 'notifications' as TabKey, label: 'Notifications', icon: <Bell size={14} /> }] : []),
        ...(canSeeEnvois ? [{ key: 'envois' as TabKey, label: 'Envois automatiques', icon: <Mail size={14} /> }] : []),
        ...(canSeeApplications ? [{ key: 'applications' as TabKey, label: 'Application', icon: <Plug size={14} /> }] : []),
        ...(canSeeDocuments ? [{ key: 'documentation' as TabKey, label: 'Démarches client', icon: <FileText size={14} /> }] : []),
    ];

    // Si l'onglet actuellement sélectionné devient indisponible (modules pas encore chargés au premier
    // rendu, puis un tab non "compte" s'avère non autorisé une fois chargés), on revient sur Compte.
    React.useEffect(() => {
        if (modulesLoaded && tab !== 'compte' && !tabs.some((t) => t.key === tab)) {
            setTab('compte');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modulesLoaded, canSeeFacturation, canSeeEInvoicing, canSeeTresorerie, canSeeCommerciaux, canSeeAbsences, canSeeHoraires, canSeeNotifications, canSeeApplications, canSeeDocuments]);

    return (
        <div className="space-y-8">
            <header>
                <h1 className="text-3xl font-bold tracking-tight text-content flex items-center gap-2"><Settings size={24} /> Paramètres</h1>
                <p className="text-content-muted mt-1">Réglages de votre compte et de votre établissement.</p>
            </header>

            <div className="bg-surface border border-border rounded-2xl p-2 inline-flex flex-wrap gap-2">
                {tabs.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === t.key ? 'bg-brand-600 text-brand-fg' : 'text-content-muted hover:text-content'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'compte' && <AccountTab />}
            {tab === 'facturation' && canSeeFacturation && <BillingSettingsModal open embedded onClose={() => {}} />}
            {tab === 'einvoicing' && canSeeEInvoicing && <EInvoicingSettings />}
            {tab === 'tresorerie' && canSeeTresorerie && <TresorerieSettings />}
            {tab === 'commerciaux' && canSeeCommerciaux && <ReglagesCommerciauxPanneau />}
            {tab === 'fidelite' && canSeeFidelite && <FideliteSettings />}
            {tab === 'absences' && canSeeAbsences && <AbsenceCodesSettings />}
            {tab === 'horaires' && canSeeHoraires && <PlanningRdvHoursTab />}
            {tab === 'interventions' && canSeeHoraires && <InterventionsTab />}
            {tab === 'frais_km' && canSeeHoraires && <FraisKilometriquesTab />}
            {tab === 'notifications' && canSeeNotifications && (
                <NotificationsOnglet activeModules={activeModules} clientType={user?.client_type}
                    voirPrestation={canSeePrestationNotif} voirAutres={canSeeClientEmailNotif} />
            )}
            {tab === 'envois' && canSeeEnvois && <EnvoisAutoSettings hasModule={hasModule} />}
            {tab === 'applications' && canSeeApplications && (
                <div className="space-y-8">
                    <ApplicationsSettings />
                    {canSeeHoraires && <BookingPaymentRuleSettings />}
                </div>
            )}
            {/* RDV Pro ouvert au Prestataire (2026-09-01) : un rendez-vous simple (devis, visite technique...)
                ne doit pas dépendre du module "Planning (rendez-vous)", réservé aux prestations facturées —
                les deux débloquent la prise de RDV sur une Démarche, cf. isRdvModuleActiveForOwner côté serveur. */}
            {tab === 'documentation' && canSeeDocuments && <DocumentTemplatesSettings canOfferAppointment={hasModule('rdv_pro') || (hasModule('planning_rdv') && user?.client_type === 'prestation')} isPrestation={user?.client_type === 'prestation'} />}
        </div>
    );
};

export default SettingsPage;
